import React, { FormEvent, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Building2,
  CalendarDays,
  ChevronRight,
  Compass,
  Fish,
  Lightbulb,
  Mail,
  Menu,
  ShieldCheck,
  X,
} from "lucide-react";
import { session as authSession } from "../../auth";
import { landingHero } from "../landingHero";
import { landingCarp } from "../landingCarp";

type EnquiryForm = {
  name: string;
  fishery: string;
  email: string;
  phone: string;
  message: string;
  company_website: string;
};

const emptyForm: EnquiryForm = {
  name: "",
  fishery: "",
  email: "",
  phone: "",
  message: "",
  company_website: "",
};

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a href="/" className="flex items-center gap-3">
      <img
        src="/icons/icon-192.png"
        alt=""
        className={compact ? "size-9 rounded-lg" : "size-11 rounded-xl"}
      />
      <div>
        <div className="font-semibold tracking-tight text-primary">CatchCompass</div>
        <div className="text-[11px] text-secondary">A point in the right direction</div>
      </div>
    </a>
  );
}

function PublicHeader({ accountReady }: { accountReady: boolean }) {
  const [open, setOpen] = useState(false);
  const href = accountReady ? "/app" : "/login";
  const label = accountReady ? "Open CatchCompass" : "Sign in";

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-background/90 backdrop-blur-xl">
      <div className="mx-auto flex min-h-16 max-w-[1200px] items-center justify-between gap-4 px-4">
        <Brand compact />
        <nav className="hidden items-center gap-6 md:flex">
          <a className="text-sm text-secondary hover:text-primary" href="/">Home</a>
          <a className="text-sm text-secondary hover:text-primary" href="#how-it-works">How it works</a>
          <a className="text-sm text-secondary hover:text-primary" href="#venue">CatchCompass Venue</a>
          <a className="text-sm text-secondary hover:text-primary" href="#enquire">Enquire</a>
          <a href={href} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
            {label}
          </a>
        </nav>
        <button
          type="button"
          className="grid size-10 place-items-center rounded-lg border border-border text-primary md:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open ? (
        <nav className="border-t border-white/10 bg-raised px-4 py-4 md:hidden">
          <div className="mx-auto flex max-w-[1200px] flex-col gap-2">
            {[
              ["Home", "/"],
              ["How it works", "#how-it-works"],
              ["CatchCompass Venue", "#venue"],
              ["Enquire", "#enquire"],
            ].map(([item, link]) => (
              <a
                key={item}
                href={link}
                className="rounded-lg px-3 py-3 text-sm font-medium text-secondary hover:bg-inset hover:text-primary"
                onClick={() => setOpen(false)}
              >
                {item}
              </a>
            ))}
            <a href={href} className="mt-2 rounded-lg bg-accent px-4 py-3 text-center text-sm font-semibold text-accent-fg">
              {label}
            </a>
          </div>
        </nav>
      ) : null}
    </header>
  );
}

function LandingPage() {
  const authQuery = useQuery({
    queryKey: ["public-auth-state"],
    queryFn: () => authSession.status(),
    staleTime: 30_000,
  });
  const accountReady =
    authQuery.data?.status === "ready" && Boolean(authQuery.data?.user);
  const accountHref = accountReady ? "/app" : "/login";
  const accountLabel = accountReady ? "Open CatchCompass" : "Sign in to CatchCompass";

  const [form, setForm] = useState<EnquiryForm>(emptyForm);
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "CatchCompass | Fishing Intelligence";
  }, []);

  const update = (key: keyof EnquiryForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  async function sendEnquiry(event: FormEvent) {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setFormError("");
    try {
      const response = await fetch("/public-api/venue-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, started_at: startedAt }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(
          result.error ||
            "We couldn't send your enquiry. Please try again or email support@catchcompass.co.uk.",
        );
      setSent(true);
      setForm(emptyForm);
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : "We couldn't send your enquiry.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-dvh bg-background text-primary">
      <PublicHeader accountReady={accountReady} />
      <main>
        <section className="relative isolate flex min-h-[760px] items-center overflow-hidden pt-16">
          <img
            src={landingHero}
            alt="Angler sitting beside three rods at a misty lake at sunrise"
            className="absolute inset-0 -z-30 size-full object-cover object-[62%_center]"
          />
          <div className="absolute inset-0 -z-20 bg-gradient-to-r from-background via-background/85 to-background/15" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-transparent to-background/20" />

          <div className="mx-auto w-full max-w-[1200px] px-4 py-20">
            <div className="max-w-3xl">
              <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-background/65 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-accent backdrop-blur">
                <Compass className="size-4" /> A point in the right direction
              </p>
              <h1 className="text-5xl font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                Fishing intelligence that learns from{" "}
                <span className="text-accent">real sessions.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-secondary sm:text-lg">
                CatchCompass helps anglers make better decisions by combining session
                data, fishing conditions and practical angling knowledge into
                evidence-based advice.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href={accountHref} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-accent px-6 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
                  {accountLabel} <ChevronRight className="size-4" />
                </a>
                <a href="#how-it-works" className="inline-flex min-h-12 items-center justify-center rounded-lg border border-border bg-background/60 px-6 text-sm font-semibold backdrop-blur hover:bg-inset">
                  Learn more
                </a>
              </div>
              <p className="mt-5 text-sm text-tertiary">
                Built to help anglers understand the pattern, not just guess the answer.
              </p>
            </div>

          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-20 border-y border-white/10 bg-raised/45 py-20">
          <div className="mx-auto max-w-[1200px] px-4">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">How it works</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">A simple process. Smarter sessions.</h2>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-3">
              <ProcessCard number="01" icon={<CalendarDays />} title="Plan" text="Record your session, target species and setup so CatchCompass can understand what you're trying to achieve." />
              <ProcessCard number="02" icon={<Fish />} title="Fish" text="Record catches, conditions and important session observations while you fish." />
              <ProcessCard number="03" icon={<Lightbulb />} title="Learn" text="Turn your fishing history into useful patterns and evidence-based advice that strengthens as the data grows." />
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto grid max-w-[1200px] gap-8 px-4 lg:grid-cols-[.9fr_1.05fr_.8fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Why CatchCompass</p>
              <h2 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
                More than just <span className="text-accent">numbers.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-7 text-secondary">
                CatchCompass doesn't just give you an answer, it explains the pattern.
                Using your data, community insight and proven angling knowledge, it
                helps you understand what's working, why it's working, and how to apply
                it on your next session.
              </p>
            </div>

            <div className="overflow-hidden rounded-3xl border border-white/10 bg-raised shadow-2xl">
              <img
                src={landingCarp}
                alt="Large carp swimming underwater in green sunlit water"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>

            <div className="space-y-4">
              <Feature icon={<BarChart3 />} title="Evidence first" text="Uses personal, community and trusted external data." />
              <Feature icon={<Lightbulb />} title="Explains the pattern" text="Not just an answer, but the why behind it." />
              <Feature icon={<ShieldCheck />} title="Independent" text="No influence from bait or tackle brands." />
            </div>
          </div>
        </section>

        <section id="venue" className="scroll-mt-20 border-y border-accent/20 bg-[radial-gradient(circle_at_75%_20%,rgb(201_162_74_/_0.12),transparent_24rem)] py-24">
          <div className="mx-auto grid max-w-[1200px] gap-10 px-4 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
            <div>
              <span className="inline-flex rounded-full border border-accent/35 bg-accent/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-accent">Coming soon</span>
              <h2 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">CatchCompass Venue</h2>
              <p className="mt-5 max-w-2xl text-xl font-medium leading-8">Venue-specific fishing intelligence &amp; fishery management insights.</p>
              <p className="mt-4 max-w-2xl text-base leading-7 text-secondary">Helping fisheries better understand their waters and give anglers better information.</p>
              <a href="#enquire" className="mt-8 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-accent px-6 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
                Enquire now <ChevronRight className="size-4" />
              </a>
            </div>
            <div className="rounded-3xl border border-white/10 bg-raised/70 p-7 shadow-xl">
              <Building2 className="size-10 text-accent" />
              <p className="mt-8 text-xs uppercase tracking-[0.18em] text-tertiary">For fisheries</p>
              <p className="mt-2 text-2xl font-semibold">Better understanding of what is happening on the water.</p>
              <p className="mt-4 text-sm leading-6 text-secondary">CatchCompass Venue is being developed with real fisheries and anglers in mind.</p>
            </div>
          </div>
        </section>

        <section id="enquire" className="scroll-mt-20 py-24">
          <div className="mx-auto grid max-w-[1200px] gap-10 px-4 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              <Mail className="size-10 text-accent" />
              <h2 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">Interested in CatchCompass Venue?</h2>
              <p className="mt-4 text-base leading-7 text-secondary">If you run or manage a fishery and would like to hear more about CatchCompass Venue, send us a message.</p>
              <p className="mt-5 text-sm text-tertiary">
                Or email <a href="mailto:support@catchcompass.co.uk" className="font-medium text-accent hover:underline">support@catchcompass.co.uk</a>
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-raised p-5 sm:p-7">
              {sent ? (
                <div className="py-12 text-center">
                  <div className="mx-auto grid size-12 place-items-center rounded-full bg-accent/10 text-accent"><Mail /></div>
                  <h3 className="mt-5 text-2xl font-semibold">Thanks for getting in touch.</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-secondary">We've received your enquiry and will be in touch soon.</p>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={sendEnquiry}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <PublicField label="Name" value={form.name} onChange={(value) => update("name", value)} autoComplete="name" required />
                    <PublicField label="Fishery / company name" value={form.fishery} onChange={(value) => update("fishery", value)} autoComplete="organization" required />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <PublicField label="Email address" type="email" value={form.email} onChange={(value) => update("email", value)} autoComplete="email" required />
                    <PublicField label="Phone number" type="tel" value={form.phone} onChange={(value) => update("phone", value)} autoComplete="tel" />
                  </div>
                  <label className="block text-sm font-medium">
                    Message
                    <textarea
                      rows={6}
                      minLength={10}
                      maxLength={3000}
                      required
                      value={form.message}
                      onChange={(event) => update("message", event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                      placeholder="Tell us a little about your fishery and what you'd like to know."
                    />
                  </label>
                  <label className="absolute -left-[10000px] top-auto size-px overflow-hidden" aria-hidden="true">
                    Company website
                    <input tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(event) => update("company_website", event.target.value)} />
                  </label>
                  {formError ? <p className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error" role="alert">{formError}</p> : null}
                  <button type="submit" disabled={sending} className="inline-flex min-h-12 w-full items-center justify-center rounded-lg bg-accent px-5 text-sm font-semibold text-accent-fg hover:bg-accent-hover disabled:opacity-60 sm:w-auto">
                    {sending ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-raised/45 py-16">
          <div className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-4 text-center sm:flex-row sm:text-left">
            <div>
              <h2 className="text-2xl font-semibold">Already using CatchCompass?</h2>
              <p className="mt-2 text-sm text-secondary">Sign in to access your CatchCompass account and fishing history.</p>
            </div>
            <a href={accountHref} className="inline-flex min-h-12 w-full items-center justify-center rounded-lg bg-accent px-6 text-sm font-semibold text-accent-fg sm:w-auto">
              {accountReady ? "Open CatchCompass" : "Sign in"}
            </a>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function ProcessCard({ number, icon, title, text }: { number: string; icon: React.ReactNode; title: string; text: string }) {
  return (
    <article className="rounded-2xl border border-border bg-background/70 p-6">
      <div className="flex items-center justify-between">
        <div className="grid size-11 place-items-center rounded-xl bg-accent/10 text-accent [&_svg]:size-5">{icon}</div>
        <span className="text-xs font-semibold tracking-[0.18em] text-tertiary">{number}</span>
      </div>
      <h3 className="mt-8 text-xl font-semibold">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-secondary">{text}</p>
    </article>
  );
}

function Feature({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex gap-4 rounded-xl border border-white/10 bg-raised/55 p-4">
      <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent [&_svg]:size-5">{icon}</div>
      <div><h3 className="font-semibold">{title}</h3><p className="mt-1 text-sm leading-6 text-secondary">{text}</p></div>
    </div>
  );
}

function PublicField({ label, type = "text", value, onChange, autoComplete, required = false }: { label: string; type?: string; value: string; onChange: (value: string) => void; autoComplete?: string; required?: boolean }) {
  return (
    <label className="block text-sm font-medium">
      {label}
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete} required={required} maxLength={254} className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
    </label>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-white/10 bg-[#0b100a]">
      <div className="mx-auto grid max-w-[1200px] gap-8 px-4 py-10 sm:grid-cols-[1fr_auto] sm:items-end">
        <Brand />
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-secondary sm:justify-end">
          <a href="/privacy" className="hover:text-primary">Privacy</a>
          <a href="/terms" className="hover:text-primary">Terms</a>
          <a href="mailto:support@catchcompass.co.uk" className="hover:text-primary">Contact</a>
          <a href="/login" className="hover:text-primary">Sign in</a>
        </div>
      </div>
      <div className="border-t border-white/10 px-4 py-4 text-center text-xs text-tertiary">© 2026 CatchCompass. All rights reserved.</div>
    </footer>
  );
}

function LegalPage({ kind }: { kind: "privacy" | "terms" }) {
  useEffect(() => {
    document.title = kind === "privacy" ? "Privacy | CatchCompass" : "Terms | CatchCompass";
  }, [kind]);
  const privacy = kind === "privacy";
  return (
    <div className="min-h-dvh bg-background text-primary">
      <header className="border-b border-white/10 bg-raised/80">
        <div className="mx-auto flex min-h-16 max-w-[1000px] items-center justify-between px-4">
          <Brand compact />
          <a href="/" className="text-sm font-medium text-accent hover:underline">Back to home</a>
        </div>
      </header>
      <main className="mx-auto max-w-[760px] px-4 py-14">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">CatchCompass</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight">{privacy ? "Privacy notice" : "Website terms"}</h1>
        <p className="mt-3 text-sm text-tertiary">Last updated: 21 September 2026</p>
        <div className="mt-10 space-y-8 text-sm leading-7 text-secondary">
          {privacy ? (
            <>
              <LegalSection title="Information we use">CatchCompass uses account information and the fishing information you choose to record to provide the service. Venue enquiry details are used to respond to the enquiry.</LegalSection>
              <LegalSection title="How information is used">Information is used to operate CatchCompass, secure accounts, provide fishing features, improve the service and respond to enquiries. CatchCompass does not sell personal information to advertisers.</LegalSection>
              <LegalSection title="Your choices">Account holders can manage their account and, where available, permanently delete their CatchCompass account and personal fishing data from within the service.</LegalSection>
              <LegalSection title="Contact">Questions about privacy can be sent to support@catchcompass.co.uk.</LegalSection>
            </>
          ) : (
            <>
              <LegalSection title="Using CatchCompass">CatchCompass provides fishing information, record keeping and decision-support tools. Fishing conditions change and advice is informational rather than a guarantee of catches or outcomes.</LegalSection>
              <LegalSection title="Fishing responsibly">Users remain responsible for following fishery rules, local law, access conditions and safe angling practice.</LegalSection>
              <LegalSection title="Accounts">Keep your login details secure and provide accurate information when using account and enquiry features.</LegalSection>
              <LegalSection title="Contact">Questions about these website terms can be sent to support@catchcompass.co.uk.</LegalSection>
            </>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section><h2 className="text-lg font-semibold text-primary">{title}</h2><p className="mt-2">{children}</p></section>;
}

export default function PublicSite() {
  const path = typeof window === "undefined" ? "/" : window.location.pathname;
  if (path === "/privacy") return <LegalPage kind="privacy" />;
  if (path === "/terms") return <LegalPage kind="terms" />;
  return <LandingPage />;
}
